import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";
import { BasicFieldInfo } from "./components/BasicFieldInfo";
import { DatasetSelector } from "./components/DatasetSelector";
import { FilterSelector } from "./components/FilterSelector";
import { toast } from "sonner";

interface FieldFormProps {
  field?: Tables<"fields"> | null;
  onClose: () => void;
}

export const FieldForm = ({ field, onClose }: FieldFormProps) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(field?.name || "");
  const [type, setType] = useState<"single_select" | "multi_select" | "nested_multi_select">(
    field?.type || "single_select",
  );
  const [primaryDatasetId, setPrimaryDatasetId] = useState(field?.primary_dataset_id || "");
  const [secondaryDatasetId, setSecondaryDatasetId] = useState(field?.secondary_dataset_id || "");
  const [filterId, setFilterId] = useState(field?.filter_id || "none");
  const [secondaryFilterId, setSecondaryFilterId] = useState(field?.secondary_filter_id || "none");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: datasets } = useQuery({
    queryKey: ["datasets"],
    queryFn: async () => {
      const { data, error } = await supabase.from("datasets").select("*").order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: primaryFilters } = useQuery({
    queryKey: ["filters", primaryDatasetId],
    queryFn: async () => {
      if (!primaryDatasetId) return [];
      const { data, error } = await supabase
        .from("filters")
        .select("*")
        .eq("dataset_id", primaryDatasetId)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!primaryDatasetId,
  });

  const { data: secondaryFilters } = useQuery({
    queryKey: ["filters", secondaryDatasetId],
    queryFn: async () => {
      if (!secondaryDatasetId) return [];
      const { data, error } = await supabase
        .from("filters")
        .select("*")
        .eq("dataset_id", secondaryDatasetId)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!secondaryDatasetId && type === "nested_multi_select",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const fieldData = {
        name,
        type,
        primary_dataset_id: primaryDatasetId || null,
        secondary_dataset_id: type === "nested_multi_select" ? secondaryDatasetId : null,
        filter_id: filterId === "none" ? null : filterId,
        secondary_filter_id: type === "nested_multi_select" && secondaryFilterId !== "none" ? secondaryFilterId : null,
      };

      console.log("Updating field with data:", fieldData);

      if (field?.id) {
        const { error } = await supabase.from("fields").update(fieldData).eq("id", field.id);

        if (error) {
          console.error("Error updating field:", error);
          throw error;
        }
      } else {
        const { error } = await supabase.from("fields").insert([fieldData]);
        if (error) throw error;
      }

      // Invalidate all related queries
      await queryClient.invalidateQueries({ queryKey: ["fields"] });
      await queryClient.invalidateQueries({ queryKey: ["field-details"] });
      await queryClient.invalidateQueries({ queryKey: ["template-fields"] });

      toast.success(field ? "Field updated successfully" : "Field created successfully");
      onClose();
    } catch (error) {
      console.error("Error saving field:", error);
      toast.error("Failed to save field");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTypeChange = (newType: "single_select" | "multi_select" | "nested_multi_select") => {
    setType(newType);
    // Reset secondary related fields if changing from nested_multi_select
    if (newType !== "nested_multi_select") {
      setSecondaryDatasetId("");
      setSecondaryFilterId("none");
    }
  };

  return (
    <Card className="p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <BasicFieldInfo name={name} setName={setName} type={type} setType={handleTypeChange} />

        <DatasetSelector
          label="Primary Dataset"
          value={primaryDatasetId}
          onChange={setPrimaryDatasetId}
          datasets={datasets}
        />

        {type === "nested_multi_select" && (
          <DatasetSelector
            label="Secondary Dataset"
            value={secondaryDatasetId}
            onChange={setSecondaryDatasetId}
            datasets={datasets}
          />
        )}

        {primaryDatasetId && (
          <FilterSelector label="Primary Filter" value={filterId} onChange={setFilterId} filters={primaryFilters} />
        )}

        {type === "nested_multi_select" && secondaryDatasetId && (
          <FilterSelector
            label="Secondary Filter"
            value={secondaryFilterId}
            onChange={setSecondaryFilterId}
            filters={secondaryFilters}
          />
        )}

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (field ? "Updating..." : "Creating...") : field ? "Update Field" : "Create Field"}
          </Button>
        </div>
      </form>
    </Card>
  );
};
